import { Loading } from '@tradingblock/components';
import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../data/global/dataSelectors';

export const DownloadComponent = ({
  row,
  accountId,
  api,
  dispatch,
  SessionActions,
  GenericErrorResponseCode,
  type,
}: {
  row: any;
  accountId: number;
  api: any;
  dispatch: any;
  SessionActions: any;
  GenericErrorResponseCode: number;
  type: 'taxDoc' | 'statement' | 'confirm';
}) => {
  // Define state for download status and error message
  const [downloadStatus, setDownloadStatus] = useState('idle'); // 'idle', 'inProgress', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const accountName = useStateSelector(
    s => (s.account.accountDetails && s.account.accountDetails.accountNames[0]) || ''
  );
  const accountNameNoSpaces = accountName.replace(/\s/g, '');
  const userRole = useStateSelector(s => s.account.roles && s.account.roles[0]);

  function extractDateFromString(input: string): string {
    // Regular expression to match the date format MM/DD/YY
    const datePattern = /\b\d{2}\/\d{2}\/\d{2}\b/;
    const match = input.match(datePattern);

    // If a date is found, return it. Otherwise, return an empty string.
    return match ? match[0] : '';
  }

  const date = extractDateFromString(row.original.message);
  const documentType = type === 'taxDoc' ? 'TaxDocument' : type === 'statement' ? 'MonthlyStatement' : 'DailyConfirm';
  let fileName = '';

  const handleDownload = () => {
    setDownloadStatus('inProgress');
    setErrorMessage('');

    const downloadFile = (result: any, finalFileName: string) => {
      // Ensure we're handling the response as a blob
      const blob = new Blob([result.data], {
        type: 'application/pdf',
      });

      // Create object URL with specific handling
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Set specific attributes to force download
      link.href = url;
      link.download = finalFileName;
      link.type = 'application/pdf';
      link.target = '_self'; // Prevent opening in new tab

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setDownloadStatus('success');
    };

    const handleError = (err: any) => {
      const statusCode = err && err.response && err.response.status;
      const responseData = err && err.response && err.response.data;
      const configUrl = err && err.config && err.config.url;
      const responseMessage = responseData && responseData.message;

      console.error('Download error details:', {
        type,
        accountId,
        documentType: row.original.documentType,
        date: row.original.received_on,
        userRole,
        error: err,
        statusCode: statusCode,
        responseData: responseData,
      });

      setDownloadStatus('error');
      let errorMessage = 'Failed to download document. Please try again later.';

      // Enhanced error messaging based on status code
      if (err && err.response) {
        switch (err.response.status) {
          case 401:
            errorMessage = 'You are not authorized to access this document. Please contact support.';
            break;
          case 403:
            errorMessage = 'You do not have permission to access this document. Please contact support.';
            break;
          case 404:
            errorMessage = 'Document not found. Please verify the document exists and try again.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later or contact support.';
            break;
          default:
            const serverMessage = responseMessage || (err.message || 'Unknown error');
            errorMessage = `Error downloading document: ${serverMessage}`;
        }
      }

      setErrorMessage(errorMessage);
      dispatch(
        SessionActions.error({
          code: GenericErrorResponseCode,
          url: configUrl || '',
          message: errorMessage,
          reason: 'Download Failed',
          details: {
            documentType: row.original.documentType,
            date: row.original.received_on,
            accountId,
            userRole,
            statusCode: statusCode,
            responseMessage: responseMessage,
          },
        })
      );
    };

    switch (type) {
      case 'taxDoc': {
        const [_, formType] = row.original.documentType.split('Form');
        api.downloads
          .getTaxDocument(accountId, formType, row.original.received_on, {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/pdf',
              'Content-Disposition': 'attachment',
            },
          })
          .then((result: any) => {
            const docType = row.original.documentType.replace(/\s/g, '');
            fileName = `${documentType}_${accountNameNoSpaces}_${date}_${docType}.pdf`;
            downloadFile(result, fileName);
          })
          .catch(handleError);
        break;
      }
      case 'statement': {
        api.downloads
          .getMonthlyStatement(accountId, row.original.received_on, {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/pdf',
              'Content-Disposition': 'attachment',
            },
          })
          .then((result: any) => {
            fileName = `${documentType}_${accountNameNoSpaces}_${date}.pdf`;
            downloadFile(result, fileName);
          })
          .catch(handleError);
        break;
      }
      case 'confirm': {
        api.downloads
          .getDailyConfirm(accountId, row.original.received_on, {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/pdf',
              'Content-Disposition': 'attachment',
            },
          })
          .then((result: any) => {
            fileName = `${documentType}_${accountNameNoSpaces}_${date}.pdf`;
            downloadFile(result, fileName);
          })
          .catch(handleError);
        break;
      }
    }
  };

  // reset download status to idle after 5 seconds
  // with cleanup function to clear the timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDownloadStatus('idle');
    }, 5000);
    return () => clearTimeout(timeout);
  }, [downloadStatus]);

  // reset error message when download status changes
  useEffect(() => {
    if (downloadStatus !== 'error') {
      setErrorMessage('');
    }
  }, [downloadStatus]);

  return (
    <>
      {downloadStatus === 'idle' && (
        <i style={{ cursor: 'pointer' }} className="fal fa-lg fa-download dashboard-icon" onClick={handleDownload} />
      )}
      {downloadStatus === 'inProgress' && <i className="fas fa-spinner fa-spin" />}
      {downloadStatus === 'error' && (
        <div>
          <i
            style={{ cursor: 'pointer', color: 'red' }}
            className="fal fa-lg fa-exclamation-triangle dashboard-icon"
            title={errorMessage}
            onClick={handleDownload}
          />
          <div className="small text-danger">{errorMessage}</div>
        </div>
      )}
    </>
  );
};
